import { DialogActions, DialogContent, Dialog as MuiDialog } from "@material-ui/core"
import { Box, Button, Checkbox, Field, Form, Select, Typography } from "components"
import { Icon } from "components/common"
import DatePicker from "components/common/DatePicker"
import dayjs from "dayjs"
import { compose, withFormik, withHooks } from "enhancers"
import { useCallback, useEffect, useState } from "react"
import styled from "styled-components"
import { AppColor } from "theme/app-color"
import { getLastDayOfMonth } from "utils/helper"
import { isEmpty } from "utils/lodash"

const Dialog = styled(MuiDialog)`
  .MuiDialog-paperScrollPaper {
    padding: 0;
    margin: 1vw;
    min-width: 343px;
    overflow: auto;
  }
`

const ModalContainer = styled(DialogContent)`
  height: 100%;
  padding: 16px;
`

const ContentContainer = styled("div")`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 100%;
  height: 100%;
`

const TitleContainer = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin: 0px auto;
  margin: 16px 0px 16px 0px;
`

const ActionContainer = styled(Box)`
  display: flex;
  width: 100%;
  padding: 0px 16px 16px 16px;
`

const ActionButton = styled(Button)`
  width: 100%;
`

interface ApproveModalProps {
  isOpen: boolean
  title: string
  handleClose: () => void
  handleConfirm: () => void
  amount: string
  isInsurance: string
  defaultTransferDate: dayjs.Dayjs
  submitTransferFast: any
  handleChangeValue: () => void
  dateValue: any
  handleChangeCheck: () => void
  master?: any
  paymentTypeOption: any[]
  handleChangeSelectValue: () => void
  isPassAwayRequest: boolean
}

const ApproveModalComponent = (props: ApproveModalProps) => {
  return (
    <Dialog open={props.isOpen}>
      <Form>
        <ModalContainer>
          <ContentContainer>
            <Icon width="40px" height="40px" name="CheckCircle" color={AppColor.Success} />
            <TitleContainer>
              <Typography variant="h3" color={AppColor["Text/Primary Text"]}>
                ยอมรับการเบิกสวัสดิการ ?
              </Typography>
            </TitleContainer>
            <Box mb="4px">
              <Typography variant="body1" color={AppColor["Text/Placeholder"]}>
                อนุมัติการเบิก
              </Typography>
            </Box>
            <Box mb="16px">
              <Typography
                variant="h5"
                style={{ fontWeight: 500, lineHeight: "24px" }}
                color={AppColor["Primary/Primary Text"]}
              >
                {props.title + " " + props.amount + " "}
                บาท
              </Typography>
            </Box>
          </ContentContainer>
          {props.isPassAwayRequest && props.paymentTypeOption.length > 0 && (
            <>
              <Box display="flex">
                <Typography variant="body1" color="Text/Black2">
                  วิธีการจ่าย
                </Typography>
                <Typography variant="body1" color="Other/Danger">
                  *
                </Typography>
              </Box>
              <Field
                component={Select}
                style={{ marginTop: 8 }}
                name="payment"
                type="text"
                placeholder="วิธีการจ่าย"
                fullWidth
                options={props.paymentTypeOption}
              />
              <br />
            </>
          )}

          <Form>
            <Box pb="8px">
              <Box display="flex" style={{ marginBottom: "8px" }}>
                <Typography variant="body1" color="Text/Black2">
                  โอนเงินเข้าบัญชีเงินเดือนพนักงานวันที่
                </Typography>
                <Typography variant="body1" color="Other/Danger">
                  *
                </Typography>
              </Box>
              <Field component={DatePicker} name="transferDate" placeholder="วันที่" fullWidth />
            </Box>
            {props.isPassAwayRequest && (
              <Box display="flex">
                <Typography variant="body1" color="Text/Gray">
                  ตามใบรับเงินที่ผู้รับผลประโยชน์เซ็นต์รับ
                </Typography>
              </Box>
            )}
            {props.submitTransferFast && (
              <Box pt={3}>
                <Field
                  p={1}
                  component={Checkbox}
                  name="submitTransferFast"
                  className="submitTransferFast"
                  label="จ่ายเป็นกรณีเร่งด่วน"
                  onChange={props.handleChangeCheck}
                />
              </Box>
            )}
          </Form>
        </ModalContainer>
        <DialogActions>
          <ActionContainer>
            <ActionButton
              variant="outlined"
              onClick={props.handleClose}
              mr="16px"
              startIcon={<Icon name="cancel" color={`${AppColor["Primary/Primary Text"]}`} />}
            >
              <Typography variant="body1">ปิดหน้าต่างนี้</Typography>
            </ActionButton>
            <ActionButton variant="contained" type="submit" startIcon={<Icon name="Check_circle" />}>
              <Typography variant="body1">ยืนยัน</Typography>
            </ActionButton>
          </ActionContainer>
        </DialogActions>
      </Form>
    </Dialog>
  )
}

const enhancer = compose(
  withFormik({}),
  withHooks((props: any, hooks: any) => {
    const { useMemo, useHandleSubmit } = hooks
    const {
      isOpen,
      onClose,
      title,
      onConfirm,
      amount,
      isInsurance,
      defaultTransferDate,
      submitTransferFast,
      paymentMethod,
      setFieldValue,
      values,
      isPassAwayRequest,
      resetForm,
      setFieldError,
    } = props

    const [checked, setChecked] = useState(false)

    const paymentTypeOption = useMemo(() => {
      if (isEmpty(paymentMethod)) {
        return []
      }

      return paymentMethod.map((payment: any) => {
        return {
          label: payment,
          value: payment,
        }
      })
    }, [paymentMethod])

    const defaultDateValue = useMemo(() => {
      const currentDate = dayjs()
      const today = currentDate.get("date")
      const defaultDayOfMonth = 25

      if (today <= defaultDayOfMonth) {
        return currentDate.set("date", defaultDayOfMonth)
      }
      return currentDate.add(1, "month").set("date", defaultDayOfMonth)
    }, [])

    // TODO: Check this logic
    const [dateValue, setDateValue] = useState(defaultDateValue)
    useEffect(() => {
      setFieldValue("transferDate", dateValue)
    }, [])

    const handleChangeCheck = useCallback((event: any) => {
      setChecked(event.target.checked)
    }, [])

    const handleClose = useCallback(() => {
      setTimeout(() => {
        if (isEmpty(paymentTypeOption)) {
          setFieldValue("payment", "โอนเงินเข้าบัญชีเงินเดือน")
        }
        resetForm()
        setFieldValue("transferDate", defaultDateValue)
      }, 500)
      onClose()
    }, [onClose, paymentTypeOption, resetForm, setFieldValue, defaultDateValue])

    useHandleSubmit(
      async (values: any) => {
        const dateWithFormat = dayjs(values.transferDate).format("YYYY-MM-DD")
        if (
          (values["payment"] === null || values["payment"] === undefined) &&
          isPassAwayRequest &&
          paymentTypeOption.length > 0
        ) {
          setFieldError("payment", "ต้องไม่เว้นว่าง")
        } else {
          handleClose()
          onConfirm(dateWithFormat, submitTransferFast ? checked : undefined, values["payment"] || undefined)
        }
      },
      [onConfirm],
    )

    useEffect(() => {
      setChecked(false)
    }, [])

    return {
      isOpen,
      handleClose,
      title,
      amount,
      isInsurance,
      defaultTransferDate,
      submitTransferFast,
      dateValue,
      handleChangeCheck,

      paymentTypeOption,

      isPassAwayRequest,
    }
  }),
)

const ApproveModal = enhancer(ApproveModalComponent)

export default ApproveModal
